import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    // Load cart from localStorage on init
    const savedCart = localStorage.getItem('rezvani_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('rezvani_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1, selectedColor = null, customizations = {}, unitPrice = null) => {
    const color = selectedColor || product.colors[0];
    const customKey = Object.entries(customizations).sort().map(([k,v]) => `${k}:${v}`).join('|');
    const effectiveUnitPrice = typeof unitPrice === 'number' ? unitPrice : product.price;
    const existingItem = cartItems.find(
      item => item.id === product.id && item.selectedColor === color && (item.customKey || '') === customKey
    );

    if (existingItem) {
      setCartItems(
        cartItems.map(item =>
          item.cartId === existingItem.cartId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        )
      );
      toast.success('Updated cart!', {
        description: `${product.name} quantity updated`
      });
    } else {
      setCartItems([
        ...cartItems,
        {
          ...product,
          price: effectiveUnitPrice,
          basePrice: product.price,
          quantity,
          selectedColor: color,
          customizations,
          customKey,
          cartId: `${product.id}-${color}-${customKey}-${Date.now()}`
        }
      ]);
      toast.success('Added to cart!', {
        description: `${product.name} has been added to your cart`
      });
    }
  };

  const removeFromCart = (cartId) => {
    const item = cartItems.find(item => item.cartId === cartId);
    setCartItems(cartItems.filter(item => item.cartId !== cartId));
    if (item) {
      toast.success('Removed from cart', {
        description: `${item.name} has been removed`
      });
    }
  };

  const updateQuantity = (cartId, quantity) => {
    if (quantity < 1) {
      removeFromCart(cartId);
      return;
    }
    setCartItems(
      cartItems.map(item =>
        item.cartId === cartId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    toast.success('Cart cleared');
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
